import { Button, DatePicker, Form, Input, InputNumber, Steps, message } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '../../components/EmptyState';
import { newClientToken } from '../../lib/client-token';
import { ApiError } from '../../lib/http';
import { isBookable, sourceLabel } from '../../lib/status';
import { labsApi, periodsApi, reservationsApi } from '../../services/resources';
import type { Lab, Period, Reservation } from '../../types/api';

export function TeacherReservationNewPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [occ, setOcc] = useState<Reservation[]>([]);
  const [lab, setLab] = useState<Lab | null>(null);
  const [date, setDate] = useState('');
  const [periodIds, setPeriodIds] = useState<string[]>([]);
  const [form, setForm] = useState({ className: '', people: 40, content: '', tools: '' });
  const [conflict, setConflict] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([labsApi.list({ page: 1, limit: 50 }), periodsApi.list()]).then(([l, p]) => {
      setLabs(l.items.filter((x) => isBookable(x)));
      setPeriods(p);
    });
  }, []);

  useEffect(() => {
    if (!lab || !date) return;
    reservationsApi
      .list({ lab_id: lab.id, date_from: date, date_to: date, page: 1, limit: 100 })
      .then((p) => setOcc(p.items.filter((x) => x.status === 'pending' || x.status === 'approved')));
  }, [lab, date]);

  const busy = (pid: string) => occ.find((o) => o.period_ids.includes(pid));

  async function submit() {
    if (!lab) return;
    try {
      await reservationsApi.create({
        lab_id: lab.id,
        date,
        period_ids: periodIds,
        remark: `${form.className} ${form.people}人 ${form.content} 器材:${form.tools}`,
        client_token: newClientToken(),
      });
      nav('/teacher/schedule');
    } catch (e) {
      if (e instanceof ApiError && e.code === 40909) {
        setConflict('该时段已占用');
        setStep(1);
      } else {
        message.error(e instanceof ApiError ? e.message : '提交失败');
      }
    }
  }

  if (step === 0 && labs.length === 0) {
    return <EmptyState text="当前没有已核验开放的实验室。" />;
  }

  return (
    <div className="stack">
      <h1 className="page-title">预约实验室</h1>
      <Steps current={step} items={[{ title: '实验室' }, { title: '节次' }, { title: '内容' }, { title: '预览' }]} />
      {step === 0 ? (
        <div className="counts">
          {labs.map((l) => (
            <button
              key={l.id}
              type="button"
              className="count-card touch"
              onClick={() => {
                setLab(l);
                setStep(1);
              }}
            >
              <span className="mono">{l.room_code}</span>
              <div>{l.name}</div>
              <div className="meta">容量 {l.capacity ?? '—'}</div>
            </button>
          ))}
        </div>
      ) : null}
      {step === 1 ? (
        <Form layout="vertical">
          <Form.Item label="日期" required>
            <DatePicker onChange={(_, s) => setDate(Array.isArray(s) ? s[0] : s)} />
          </Form.Item>
          {conflict ? <p className="muted">{conflict}，请改选节次。</p> : null}
          <div className="slot-grid">
            {periods.map((p) => {
              const hit = busy(p.id);
              const selected = periodIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className="slot-cell"
                  disabled={!!hit}
                  onClick={() =>
                    setPeriodIds((ids) => (ids.includes(p.id) ? ids.filter((x) => x !== p.id) : [...ids, p.id]))
                  }
                >
                  {p.name}
                  {selected ? ' · 已选' : ''}
                  {hit ? ` · ${sourceLabel(hit.source)} ${hit.user_name ?? ''}占用` : ''}
                </button>
              );
            })}
          </div>
          <Button className="touch" type="primary" disabled={!date || periodIds.length === 0} onClick={() => setStep(2)}>
            下一步
          </Button>
        </Form>
      ) : null}
      {step === 2 ? (
        <Form layout="vertical">
          <Form.Item label="班级">
            <Input value={form.className} onChange={(e) => setForm({ ...form, className: e.target.value })} />
          </Form.Item>
          <Form.Item label="人数">
            <InputNumber min={1} value={form.people} onChange={(v) => setForm({ ...form, people: v ?? 1 })} />
          </Form.Item>
          <Form.Item label="实验内容">
            <Input.TextArea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          </Form.Item>
          <Form.Item label="器材">
            <Input value={form.tools} onChange={(e) => setForm({ ...form, tools: e.target.value })} />
          </Form.Item>
          <Button className="touch" type="primary" onClick={() => setStep(3)}>
            预览
          </Button>
        </Form>
      ) : null}
      {step === 3 ? (
        <div className="surface" style={{ padding: 16 }}>
          <p>实验室 {lab?.room_code} {lab?.name}</p>
          <p>
            {date} {periodIds.length} 节
          </p>
          <p>
            {form.className} {form.people} 人
          </p>
          <p>{form.content}</p>
          <Button className="touch" type="primary" onClick={() => void submit()}>
            提交预约
          </Button>
        </div>
      ) : null}
    </div>
  );
}
